import React, { Fragment, useCallback, useMemo } from "react";
import { MembershipMapbox } from "../../components";
import { Location } from "../../data";
import { Partner, PartnerCatalogCategory } from "../../data/catalog";
import { Testimonial } from "../../data/partners";
import { PartnerConfig } from "../../offering/partner_config";
import {
  BackButton,
  FooterV2,
  OfferingHeader,
  OfferingTestimonials,
} from "../components";
import { MembershipLocationsV2 } from "../components/membership_location_list";
import { MoreToExplore } from "../components/more_to_explore";
import { TalkWithUsPrompt } from "../components/talk_with_us_prompt";
import { CategoryOfferingIntro } from "../pages/category/components/offering_intro";
import { Offerings } from "../pages/category/components/offerings";
import { CatalogBlock } from "../../offering/catalog_block";
import { Spacer } from "../../offering/spacer";
import { useMediaQuery } from "../../hooks/use_media_query";
import classNames from "classnames";
import { OfferingMessageType } from "@/offering/api";
import { buildPathWithParams, useQueryString } from "@/hooks";
import { useHistory } from "react-router-dom";
import { useI18n } from "@/lib/i18n/i18n_provider";
import { useAnalytics } from "@/lib/analytics/analytics";

interface CatalogCategoryProps {
  category: PartnerCatalogCategory;
  partner: Partner;
  partnerConfig: PartnerConfig;
  partnerOfferingTestimonials?: Testimonial[];
  allLocations?: Location[];
}

export function CatalogCategory(props: CatalogCategoryProps) {
  const mq = useMediaQuery();
  const {
    partner,
    category,
    partnerConfig,
    partnerOfferingTestimonials,
    allLocations,
  } = props;
  const isDesktop = mq.device === "desktop";
  const history = useHistory();
  const analytics = useAnalytics();
  const queryParams = useQueryString();

  const isMembershipCategory = category.catalogOfferings?.some(
    (off) => !!off.membership
  );
  const {
    hubspotForm,
    hubspotForms,
    localeOptions,
    showTalkWithUsPrompt = true,
    talkWithUsMessage,
    slug,
    instagramLink,
  } = partnerConfig;

  const { id: partnerId, homepageURL, logo: partnerLogo } = partner;
  const { partnerCatalog, catalogBlocks } = category;
  const hasBlocks = catalogBlocks && !!catalogBlocks.length;
  const { fbt } = useI18n();

  const goBack = useCallback(() => {
    const pathWithParams = buildPathWithParams(
      `/offerings/catalog/${partner.partnerSlug}`,
      {
        reset: true,
        keepOnReset: ["fromCatalog", "promoCode", "email"],
      }
    );

    history.replace(pathWithParams);

    analytics.event(
      "Breadcrumb Click",
      {
        Source: "WL",
      },
      { onlyProviders: ["mixpanel", "customerio"] }
    );
  }, [analytics, history, partner.partnerSlug]);

  const filteredCategoryOfferings = useMemo(
    () =>
      queryParams.locationID
        ? category.catalogOfferings?.filter((offering) =>
            offering.locationIDs.includes(queryParams.locationID)
          )
        : category.catalogOfferings,
    [category.catalogOfferings, queryParams.locationID]
  );

  return (
    <>
      <OfferingHeader
        homepageURL={homepageURL}
        logo={partnerLogo}
        offeringID={category.id}
        partnerID={partnerId}
        showTalkWithUsPrompt={showTalkWithUsPrompt}
        hubspotForm={hubspotForm}
        talkWithUsMessage={talkWithUsMessage}
        messageableType={OfferingMessageType.PartnerCatalog}
        localeOptions={localeOptions}
        hubspotForms={hubspotForms}
      />
      <div className="container">
        <div className="py-3">
          <BackButton label={fbt("backToAllOffering")} onPress={goBack} />
        </div>
        <CategoryOfferingIntro
          title={category.title}
          htmlDescription={
            category.groupLandingPageHeaderDescription || category.description
          }
          imageSrc={category.imageSrc}
        />
        {!!filteredCategoryOfferings &&
          filteredCategoryOfferings.length > 0 && (
            <Offerings
              items={filteredCategoryOfferings}
              membership={isMembershipCategory}
              title={category.offeringCardsHeader}
            />
          )}

        {hasBlocks && partnerCatalog && (
          <Fragment>
            <div
              className={classNames("d-flex flex-column gap-5")}
              data-testid="category-blocks"
            >
              {catalogBlocks.map((b, ind) => {
                return (
                  <div key={b.id}>
                    <CatalogBlock block={b} partnerCatalog={partnerCatalog} />
                  </div>
                );
              })}
            </div>
            <Spacer size={isDesktop ? 40 : 16} />
            <div className="divider" />
            <Spacer size={isDesktop ? 40 : 16} />
          </Fragment>
        )}

        {!!allLocations && allLocations.length > 0 && (
          <MembershipLocationsV2 locations={allLocations} />
        )}
      </div>
      {!!allLocations && allLocations.length > 0 && (
        <div
          style={{ height: 364 }}
          className="container-fluid px-0"
          data-testid="mapbox"
        >
          <MembershipMapbox locationList={allLocations} />
        </div>
      )}
      {/* TESTIMONIALS */}
      {!!partnerOfferingTestimonials &&
        partnerOfferingTestimonials?.length > 0 && (
          <div className="container-fluid px-0 bg-info py-5 offerings__testimonials">
            <div className="container pt-3">
              <OfferingTestimonials
                testimonials={partnerOfferingTestimonials}
              />
            </div>
          </div>
        )}
      {/* MORE TO EXPLORE */}
      <div className="container">
        <div className="py-5">
          <MoreToExplore
            exploreMoreHeader={category.exploreMoreHeader}
            partnerSlug={slug}
            currentPartnerOffering={""}
            fromCatalog={partnerCatalog?.slug}
            categoryOfferings={category.catalogOfferings?.map(
              (off) => off.slug || ""
            )}
          />
        </div>

        {/* TALK TO US MOBILE */}
        <div className="py-1 d-flex justify-content-center d-block d-md-none mt-4">
          {showTalkWithUsPrompt ? (
            <TalkWithUsPrompt
              partnerID={partner.id}
              hubspotForm={hubspotForm}
              messageableID={category.id}
              messageableType={OfferingMessageType.PartnerCatalog}
              hubspotForms={hubspotForms}
            />
          ) : (
            talkWithUsMessage && `${talkWithUsMessage}`
          )}
        </div>

        {/* FOOTER */}
        <div className="py-4">
          <FooterV2 instagramLink={instagramLink} />
        </div>
      </div>
    </>
  );
}
